import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import {
  CDK_ROW_TEMPLATE,
  CdkFooterRow,
  CdkFooterRowDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkNoDataRow,
  CdkRow,
  CdkRowDef,
  CdkTableModule,
} from '@angular/cdk/table';

/**
 * Header row definition for the iss-table.
 * Captures the header row's template and other header properties such as the columns to display.
 */
@Directive({
  selector: '[issHeaderRowDef]',
  providers: [ { provide: CdkHeaderRowDef, useExisting: HeaderRowDefDirective } ],
  standalone: true,
})
export class HeaderRowDefDirective extends CdkHeaderRowDef {
  @Input({ alias: 'issHeaderRowDef', required: true }) public override columns!: Iterable<string>;
  @Input('issHeaderRowDefSticky') public override sticky = false;
}

/**
 * Footer row definition for the iss-table.
 * Captures the footer row's template and other footer properties such as the columns to display.
 */
@Directive({
  selector: '[issFooterRowDef]',
  providers: [ { provide: CdkFooterRowDef, useExisting: FooterRowDefDirective } ],
  standalone: true,
})
export class FooterRowDefDirective extends CdkFooterRowDef {
  @Input({ alias: 'issFooterRowDef', required: true }) public override columns!: Iterable<string>;
  @Input('issFooterRowDefSticky') public override sticky = false;
}

/**
 * Data row definition for the iss-table.
 * Captures the data row's template and other properties such as the columns to display and
 * a when predicate that describes when this row should be used.
 */
@Directive({
  selector: '[issRowDef]',
  providers: [ { provide: CdkRowDef, useExisting: RowDefDirective } ],
  standalone: true,
})
export class RowDefDirective<T> extends CdkRowDef<T> {
  @Input({ alias: 'issRowDefColumns', required: true }) public override columns!: Iterable<string>;
  // TODO: remove ! when CdkRowDef typing fixed
  @Input('issRowDefWhen') public override when!: (
    index: number,
    rowData: T,
  ) => boolean;
}

/** Header template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'tr[issHeaderRow]', // eslint-disable-line @angular-eslint/component-selector
  template: CDK_ROW_TEMPLATE,
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-header-row',
    role: 'row',
  },
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'issHeaderRow',
  providers: [ { provide: CdkHeaderRow, useExisting: HeaderRowComponent } ],
  standalone: true,
  imports: [ CdkTableModule ],
})
export class HeaderRowComponent extends CdkHeaderRow {}

/** Footer template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'tr[issFooterRow]', // eslint-disable-line @angular-eslint/component-selector
  template: CDK_ROW_TEMPLATE,
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-footer-row',
    role: 'row',
  },
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'issFooterRow',
  providers: [ { provide: CdkFooterRow, useExisting: FooterRowComponent } ],
  standalone: true,
  imports: [ CdkTableModule ],
})
export class FooterRowComponent extends CdkFooterRow {}

/** Data row template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'tr[issRow]', // eslint-disable-line @angular-eslint/component-selector
  template: CDK_ROW_TEMPLATE,
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-row',
    '[class.is-selected]': 'highlighted',
    '[class.iss-is-notification]': 'highlightVariant === "notification"',
    role: 'row',
  },
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'issRow',
  providers: [ { provide: CdkRow, useExisting: RowComponent } ],
  standalone: true,
  imports: [ CdkTableModule ],
})
export class RowComponent extends CdkRow {
  @Input() public highlighted?: boolean | null;
  @Input() public highlightVariant?: 'notification';
}

/** Data expandable row template container that contains the cell outlet. Adds the right class and role. */
@Component({
  selector: 'tr[issExpandableRow]', // eslint-disable-line @angular-eslint/component-selector
  template: CDK_ROW_TEMPLATE,
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-expandable-row',
    '[class.is-selected]': 'highlighted',
    role: 'row',
  },
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'issExpandableRow',
  providers: [ { provide: CdkRow, useExisting: ExpandableRowComponent } ],
  standalone: true,
  imports: [ CdkTableModule ],
})
export class ExpandableRowComponent extends CdkRow {
  @Input() public highlighted?: boolean;
}

/** Row that can be used to display a message when no data is shown in the table. */
@Directive({
  selector: '[issNoDataRow]',
  providers: [ { provide: CdkNoDataRow, useExisting: NoDataRowDirective } ],
  standalone: true,
})
export class NoDataRowDirective extends CdkNoDataRow {
  public override _contentClassName = 'iss-no-data-row';
}
